import { CalendarEvent } from '../services/calendarService';

export const RECRUITING_ACADEMIC_YEAR = '2026–27';

/** Sports that have an NCAA D1/D2 recruiting calendar in Polaris. */
export const NCAA_RECRUITING_SPORT_CATEGORY = 'NCAA Championship Sports';

export const EMPTY_RECRUITING_MONTH_HINT = 'No recruiting periods in this month.';

export function isNcaaRecruitingCatalogSport(category?: string | null): boolean {
    return category === NCAA_RECRUITING_SPORT_CATEGORY;
}

export const RECRUITING_PERIOD_BG: Record<string, string> = {
    'contact-period': '#e9f5ea',
    'quiet-period': '#fdf2e9',
    'dead-period': '#fde8e8',
    'recruiting-shutdown': '#f3e8ff',
    'evaluation-period': '#e0f7fa',
    'signing-date-dead-period': '#ffe4e6',
};

export const RECRUITING_PERIOD_DOT: Record<string, string> = {
    'contact-period': '#4caf50',
    'quiet-period': '#f59e0b',
    'dead-period': '#dc2626',
    'recruiting-shutdown': '#7c3aed',
    'evaluation-period': '#0891b2',
    'signing-date-dead-period': '#be123c',
};

/**
 * Lower is more restrictive. Overlapping days open and paint the exception period
 * (signing-date dead / shutdown / dead) instead of the broader contact or quiet window.
 */
export const RECRUITING_PERIOD_RESTRICTIVENESS: Record<string, number> = {
    'signing-date-dead-period': 0,
    'recruiting-shutdown': 1,
    'dead-period': 2,
    'quiet-period': 3,
    'evaluation-period': 4,
    'contact-period': 5,
};

const UNKNOWN_RESTRICTIVENESS = 100;

export function recruitingPeriodRestrictiveness(slug: string | null | undefined): number {
    if (!slug) {
        return UNKNOWN_RESTRICTIVENESS;
    }
    return RECRUITING_PERIOD_RESTRICTIVENESS[slug] ?? UNKNOWN_RESTRICTIVENESS;
}

/** Calendar date of an ISO timestamp using the stored UTC day (NCAA dates, not the browser timezone). */
export function utcCalendarDate(iso: string): Date | null {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
        return null;
    }
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

/**
 * Inclusive last calendar day for an all-day recruiting event.
 * The API uses exclusive end dates (midnight after the last NCAA day).
 */
export function inclusiveRecruitingEndIso(start: string, end?: string): string {
    if (!end) {
        return start;
    }
    const startDay = utcCalendarDate(start);
    const exclusiveEnd = utcCalendarDate(end);
    if (!startDay || !exclusiveEnd) {
        return start;
    }
    const inclusive = new Date(exclusiveEnd);
    inclusive.setUTCDate(inclusive.getUTCDate() - 1);
    if (inclusive.getTime() < startDay.getTime()) {
        return start;
    }
    return inclusive.toISOString();
}

/** US calendar date (M/D/YYYY) from the NCAA day, independent of the viewer's timezone. */
export function formatUsCalendarDate(iso: string): string {
    const d = utcCalendarDate(iso);
    if (!d) {
        return '';
    }
    return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
}

function formatRange(start: string, end?: string): string {
    const startLabel = formatUsCalendarDate(start);
    if (!startLabel) {
        return '';
    }
    if (!end) {
        return startLabel;
    }
    const endLabel = formatUsCalendarDate(inclusiveRecruitingEndIso(start, end));
    if (!endLabel || startLabel === endLabel) {
        return startLabel;
    }
    return `${startLabel} – ${endLabel}`;
}

export type RecruitingLegendItem = {
    slug: string;
    label: string;
    color: string;
    rangeLabel: string;
};

export function buildRecruitingLegendItems(events: CalendarEvent[]): RecruitingLegendItem[] {
    const bySlug = new Map<string, CalendarEvent[]>();
    for (const event of events) {
        if (event.source?.toLowerCase() !== 'recruiting' || !event.recruitingEventType) {
            continue;
        }
        const slug = event.recruitingEventType;
        const list = bySlug.get(slug) ?? [];
        list.push(event);
        bySlug.set(slug, list);
    }

    const order = [
        'contact-period',
        'quiet-period',
        'dead-period',
        'recruiting-shutdown',
        'evaluation-period',
        'signing-date-dead-period',
    ];

    return order
        .filter((slug) => bySlug.has(slug))
        .map((slug) => {
            const slugEvents = [...(bySlug.get(slug) ?? [])].sort(
                (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
            );
            const first = slugEvents[0];
            const label = first.title || slug.replace(/-/g, ' ');
            return {
                slug,
                label,
                color: RECRUITING_PERIOD_DOT[slug] ?? '#64748b',
                rangeLabel: slugEvents.map((event) => formatRange(event.start, event.end)).join('; '),
            };
        });
}

export function recruitingCalendarLabel(
    sportName?: string | null,
    division: RecruitingNcaaDivision = 'D1'
): string {
    const divisionLabel = recruitingDivisionLabel(division);
    if (sportName?.trim()) {
        return `${RECRUITING_ACADEMIC_YEAR} NCAA ${divisionLabel} ${sportName.trim()} Recruiting Calendar`;
    }
    return `${RECRUITING_ACADEMIC_YEAR} NCAA ${divisionLabel} Recruiting Calendar`;
}

export type RecruitingNcaaDivision = 'D1' | 'D2';

export function recruitingDivisionLabel(division: RecruitingNcaaDivision): string {
    return division === 'D2' ? 'Division II' : 'Division I';
}

export function recruitingEventMatchesDivision(
    event: { ncaaDivision?: string | null; description?: string | null },
    division: RecruitingNcaaDivision
): boolean {
    if (event.ncaaDivision === 'D1' || event.ncaaDivision === 'D2') {
        return event.ncaaDivision === division;
    }
    const description = event.description ?? '';
    if (/NCAA Division II/i.test(description)) {
        return division === 'D2';
    }
    if (/NCAA Division I(?!I)/i.test(description)) {
        return division === 'D1';
    }
    return true;
}
